import C from "@/components/button/WButton.module.css";

import {
    actionIsDisabled,
    actionIsEvent,
    actionIsLinkProps,
    actionIsRouterLink,
    buildHrefFromAction,
    normalizeAction,
    type Action,
    type RouterLinkProps,
} from "@/components/Action.tsx";
import { IconWrapper, type IconProperty } from "@/components/icon/IconWrapper.tsx";
import { Spinner } from "@/components/Spinner.tsx";
import { TodoButtonIcon } from "@/dev/Todo.tsx";
import { isTodoFn } from "@/dev/todoFn.ts";
import { raiseBackgroundErrorToast } from "@/modules/notifications/toast.tsx";
import { cn } from "@/utils/classNames.ts";
import { useDeepEqualMemo } from "@/utils/react/useDeepEqualMemo.ts";
import { usePromiseLoading } from "@/utils/react/usePromiseLoading.ts";
import { vi, type VariantInfoProps } from "@/utils/variants.ts";
import { Trans } from "@lingui/react/macro";
import type { FocusableElement } from "@react-types/shared";
import { useLinkProps, useRouter } from "@tanstack/react-router";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import React, {
    forwardRef,
    type ForwardedRef,
    type MouseEvent,
    type MouseEventHandler,
    type ReactNode,
    type RefAttributes,
} from "react";
import { useFocusRing, useObjectRef } from "react-aria";
import { Button, Link, type ButtonProps } from "react-aria-components";

// eslint-disable-next-line react-refresh/only-export-components
export const vars = vi({
    variants: {
        /** Color */
        color: ["default", "primary", "success", "error", "muted", "sidebar"],
        variant: ["basic", "ghost", "border", "dashed", "editable", "edit", "tableLink"],
        size: ["xs", "md", "lg", "bar", undefined],
        fontSize: ["font-size-small", "font-size-default", "font-size-subtitle", undefined],
        iconSide: ["left", "right", undefined],
    },
    defaults: {
        color: "default",
        variant: "basic",
        size: "md",
        iconSide: "left",
    },
});

type VariantProps = VariantInfoProps<typeof vars>;

export interface ButtonLookProps extends VariantProps {
    children?: ReactNode;
    className?: string;
    /** Short-hand property equivalent to {@link children} for better-looking code for simple buttons. */
    label?: ReactNode;
    isLoading?: boolean;
    isDisabled?: boolean;
    ariaLabel?: string;
    icon?: IconProperty;
    isTodo?: boolean;
    "data-testid"?: string;
}

/**
 * (set {@link slot} to `null` to prevent the button from becoming eg. a popover trigger)
 */
export interface RegularButtonProps extends ButtonLookProps, Pick<ButtonProps, "type" | "autoFocus" | "slot"> {
    slot?: string | null;
}

export interface LinkButtonProps extends ButtonLookProps {}

// XXX: It's actually either a `RegularButtonProps` or a `LinkButtonProps`, but use an interface instead because
// otherwise derived component types work badly in TypeScript (when inheriting from an exclusive union type).
export interface WButtonProps extends RegularButtonProps, LinkButtonProps {
    /** See {@link Action}. `undefined` means do nothing. */
    action: Action | undefined;
    /**
     * Use a custom `<a>` implementation for performance (useful in admin).
     * Has some accessibility deficiencies.
     */
    fastLink?: boolean;
}

export const WButton = forwardRef(function WButton(
    {
        children,
        className,
        label,
        color = vars.defaults.color,
        variant = vars.defaults.variant,
        size = vars.defaults.size,
        icon,
        iconSide = vars.defaults.iconSide,
        fontSize,
        action,
        isLoading: isLoadingProp,
        isDisabled = actionIsDisabled(action),
        ariaLabel,
        isTodo,
        fastLink,
        ...props
    }: WButtonProps,
    ref: ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
    const { isPromiseLoading, trackPromise } = usePromiseLoading();

    const isLoading = isLoadingProp ?? isPromiseLoading;

    const hasChildren = React.Children.count(children) > 0 || !!label;

    const variantCns = cn(
        C[color],
        C[variant],
        C[size],
        fontSize && C[fontSize],
        !hasChildren && C.square,
        isDisabled && C.disabled,
    );
    const commonCns = cn(C.Button, className, variantCns);

    const childrenWrapper = (
        <span key="children" className={C.buttonText}>
            {label}
            {children}
        </span>
    );

    let childrenWithIcon;
    if (icon) {
        const iconSpacer = hasChildren ? <span key="spacer" className={C.iconSpacer} /> : null;
        const iconElement = <IconWrapper key="icon" icon={icon} className={cn(C.iconWrapper, variantCns)} />;

        childrenWithIcon = [iconElement, iconSpacer, childrenWrapper];
        if (iconSide === "right") {
            childrenWithIcon.reverse();
        }
    } else {
        childrenWithIcon = [childrenWrapper];
    }

    if (import.meta.env.DEV && (isTodo ?? isTodoFn(action))) {
        childrenWithIcon.push(<TodoButtonIcon key="todoIcon" className="ml-1" />);
    }

    if (isLoading) {
        // TODO: handle `isDisabled`
        childrenWithIcon.push(<Spinner key="spinner" />);
    }

    const commonProps = {
        "data-loading": isLoading ? true : undefined,
        "aria-label": ariaLabel,
        "data-testid": props["data-testid"],
        children: childrenWithIcon,
        className: commonCns,
        isDisabled: !!isDisabled || isLoading,
    };

    action = normalizeAction(action);

    if (actionIsRouterLink(action)) {
        fastLink ??= variant === "tableLink";
        const RouterLink = fastLink ? RouterLinkFast : AriaRouterLink;

        return <RouterLink ref={ref as ForwardedRef<HTMLAnchorElement>} action={action} {...commonProps} />;
    } else if (actionIsLinkProps(action)) {
        return <Link ref={ref as ForwardedRef<HTMLAnchorElement>} {...commonProps} {...action} />;
    } else if (actionIsDisabled(action) || actionIsEvent(action) || action == null) {
        const { type, autoFocus, slot } = props as RegularButtonProps;
        return (
            <Button
                ref={ref as ForwardedRef<HTMLButtonElement>}
                {...commonProps}
                onPress={
                    action != null && !actionIsDisabled(action)
                        ? (e) =>
                              trackPromise(
                                  () => action(e),
                                  (err) => raiseBackgroundErrorToast(err, <Trans>Error performing action</Trans>),
                              )
                        : undefined
                }
                type={type}
                autoFocus={autoFocus}
                slot={slot}
                {...props}
            />
        );
    } else {
        exhaustiveSwitchCheck(action);
    }
});

interface AriaRouterLinkProps extends RefAttributes<HTMLAnchorElement> {
    className?: string;
    action: RouterLinkProps;
    isDisabled?: boolean;
    children?: ReactNode;
}

const defaultLinkProps = {
    // Trust router links by default
    rel: "opener",
} as const;

type UseLinkProps = Parameters<typeof useLinkProps>[0];

/** Component that connects Tanstack Router to react-aria's Link */
function AriaRouterLink(props: AriaRouterLinkProps) {
    // const start = performance.now();

    const { action, isDisabled } = props;

    // Building URLs in TanStack Router can be very slow, so memoize the options
    const options = useDeepEqualMemo(() => {
        return {
            ...defaultLinkProps,
            ...(action as UseLinkProps),
            disabled: isDisabled,
        };
    }, [action, isDisabled]);

    // Type of `onPress` is not suitable for Tanstack Router since it expects a mouse event in its `onClick` handler - so would need to use Tanstack's `onClick` as the outermost handler.
    // Unfortunately the <Link> component from react-aria can not use a custom `onClick` but instead always shows a deprecation warning.

    const {
        // Ignore redundant or unused attributes
        onFocus: _1,
        onBlur: _2,
        className: _3,
        // Need to cast this
        onClick,
        ...routerProps
    } = useLinkProps(options);

    // performance.measure("useLinkProps", { start });

    return (
        <Link
            {...routerProps}
            onClick={
                // NB: react-aria only gives us a `FocusableElement` but let's assume that we get an `HTMLAnchorElement` (this might break accessibility?)
                onClick as MouseEventHandler<FocusableElement>
            }
            {...props}
        />
    );
}

function isCtrlEvent(e: MouseEvent) {
    return !!(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey);
}

/** Clone of Tanstack Router's `useLinkProps` and react-aria's `<Link>` component for better performance */
function RouterLinkFast({ ref, action, isDisabled, className, children }: AriaRouterLinkProps) {
    const aRef = useObjectRef<HTMLAnchorElement>(ref);

    const router = useRouter();

    // Building URLs in TanStack Router can be very slow, so memoize the options
    const href = useDeepEqualMemo(() => {
        // const start = performance.now();

        // NB: This is non-reactive, so the link *must* not depend on the current router state
        const href = buildHrefFromAction(router, action);

        // performance.measure("buildLocation", { start });
        return href;
    }, [router, action, isDisabled]);

    const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
        const { target } = action as UseLinkProps;
        if (
            !isDisabled &&
            !isCtrlEvent(e) &&
            !e.defaultPrevented &&
            (!target || target === "_self") &&
            e.button === 0
        ) {
            e.preventDefault();
            e.stopPropagation();

            discardPromise(router.navigate(action));
        }
    };

    const { focusProps, isFocused, isFocusVisible } = useFocusRing();

    /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
    return (
        <a
            ref={aRef}
            className={className}
            data-fast="true"
            // Only way to disable an <a> element is to not have an `href`: https://www.scottohara.me/blog/2021/05/28/disabled-links.html
            href={isDisabled ? undefined : href}
            aria-disabled={isDisabled || undefined}
            data-disabled={isDisabled || undefined}
            role={isDisabled ? "link" : undefined}
            data-focused={isFocused || undefined}
            // NB: use `:hover` and `:active` pseudo-classes instead of these attributes
            // data-hovered={isHovered || undefined}
            // data-pressed={isPressed || undefined}
            data-focus-visible={isFocusVisible || undefined}
            // TODO: Need to handle `onPress` and `onKeyDown` events etc.
            onClick={onClick}
            {...defaultLinkProps}
            {...focusProps}
        >
            {children}
        </a>
    );
    /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
}
